import Point from '@arcgis/core/geometry/Point';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

import { Injectable } from '@angular/core';

import { EsriMap } from '@lib/geo/esri';
import { SnLonLat, SnLonLatBounds, SnLonLatLike, SnLonLatBoundsLike } from '@lib/geo/helpers';

import { BaseBoundsManagementService } from '../base-bounds-management.service';

@Injectable({
  providedIn: 'root',
})
export class EsriBoundsManagementService extends BaseBoundsManagementService {
  private _map?: EsriMap;

  constructor() {
    super();
  }

  public setMap(map: EsriMap): void {
    this._map = map;
  }

  public getBounds(): SnLonLatBounds {
    if (!this._map) {
      throw new Error('Map not set. Call setMap() first.');
    }
    
    const extent = this._map.mapView?.extent || this._createExtentFromCenter();
    if (!extent) {
      throw new Error('No map extent available');
    }

    const latLngBounds = webMercatorUtils.webMercatorToGeographic(extent);
    if (!latLngBounds?.extent) {
      throw new Error('Failed to convert extent to geographic coordinates');
    }

    const neBound = [latLngBounds.extent.xmax, latLngBounds.extent.ymax];
    const swBound = [latLngBounds.extent.xmin, latLngBounds.extent.ymin];
    return new SnLonLatBounds(swBound, neBound);
  }

  public fitBounds(bounds: SnLonLatBounds): void {
    if (!bounds || !this._map?.mapView) {
      return;
    }

    const extent = SnLonLatBounds.convertToExtent(bounds);
    this._map.mapView.goTo({
      target: extent,
    });
  }

  public panToBounds(bounds: SnLonLatBoundsLike): void {
    if (!bounds || !this._map?.mapView) {
      return;
    }

    // For ESRI, panToBounds is similar to fitBounds but with different options
    const boundsInstance = SnLonLatBounds.convert(bounds);
    const extent = SnLonLatBounds.convertToExtent(boundsInstance);
    this._map.mapView.goTo({
      target: extent,
      duration: 1000, // Smooth pan animation
    });
  }

  public contains(lonLatLike: SnLonLatLike): boolean {
    if (!this._map?.mapView?.extent && !this._map?.sceneView?.extent) {
      return false;
    }

    const lonLat = SnLonLat.convert(lonLatLike);
    const extent = this._map?.mapView?.extent || this._map?.sceneView?.extent;
    
    const latLngBounds = webMercatorUtils.webMercatorToGeographic(extent);
    if (!latLngBounds?.extent?.contains) {
      return false;
    }

    const point = new Point({
      latitude: lonLat.lat,
      longitude: lonLat.lon,
    });
    return latLngBounds.extent.contains(point);
  }

  private _createExtentFromCenter(): any {
    if (!this._map?.mapView) {
      return null;
    }

    const center = this._map.mapView.center;
    const resolution = this._map.mapView.resolution;
    const mapWidthInPixels = this._map.mapView.width;
    const mapHeightInPixels = this._map.mapView.height;

    const extentWidth = mapWidthInPixels * resolution;
    const extentHeight = mapHeightInPixels * resolution;

    const xmin = center.x - extentWidth / 2;
    const ymin = center.y - extentHeight / 2;
    const xmax = center.x + extentWidth / 2;
    const ymax = center.y + extentHeight / 2;

    return {
      xmin: xmin,
      ymin: ymin,
      xmax: xmax,
      ymax: ymax,
      spatialReference: { wkid: 4326 },
    };
  }
}
